import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useAuth } from "@clerk/clerk-react";

import useAmplitude from "./useAmplitude";
import { ensureSocket, getSocket } from "../socketClient";
import {
  updateDigestContentItem,
  setDigestServerHead,
} from "../reducers/digestReducer";
import { addEmailFileId } from "../reducers/chatReducer";
import {
  addJobs,
  addRetryJobs,
  clearManualMailFailures,
  jobDone,
  jobFail,
  resetJobs,
  setManualMailFailureMailbox,
} from "../reducers/emailJobReducer";
import {
  getEmailActionStatusesService,
  queueUserEmailActionService,
} from "../utils/emailService";
import { getTaskByIdService } from "../utils/taskService";
import { getRemainingEmailsToProcess } from "../utils/emailActions";
import { createTask } from "../reducers/taskReducer";
import { createMaintenanceTicket } from "../reducers/maintenanceTicketReducer";
import useEventCallback from "./useEventCallback";
import {
  normalizeSafeMailFailure,
  pickSafeMailFailureFields,
} from "../utils/mailFailurePresentation";

const QUEUE_REQUEST_JOB_ID = 'queue-request';
const JOB_RECONCILIATION_INTERVAL_MS = 2000;
const JOB_RECONCILIATION_TIMEOUT_MS = 5 * 60 * 1000;
const newBatchRequestId = () => (
  window.crypto?.randomUUID?.()
  ?? `mail-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

export const useIterativeDigestEmailActions = ({ itemsToRender, currentDigest }) => {
  const dispatch = useDispatch();
  const { getToken } = useAuth();
  const socketRef = useRef(null);
  const socketHandlersRef = useRef(null);
  const jobIdsRef = useRef([]);
  const batchRequestRef = useRef(null);
  const batchOutcomeRef = useRef(null);
  const batchFailedRef = useRef(false);
  const batchContextRef = useRef(null);
  const preserveFailuresRef = useRef(false);
  const reconciliationTimerRef = useRef(null);
  const eventProcessingRef = useRef(Promise.resolve());
  const { trackEvent } = useAmplitude();

  const assistant = useSelector(
    (state) => state.assistant || { assistant: null },
  );

  const [isProcessingEmailActions, setIsProcessingEmailActions] =
    useState(false);
  const [currentEmailProcessingIndex, setCurrentEmailProcessingIndex] =
    useState(0);
  const [processedEmails, setProcessedEmails] = useState([]);
  const [emailsToProcess, setEmailsToProcess] = useState([]);
  const [isProcessingEvents, setIsProcessingEvents] = useState(false);

  // Flatten the grouped emails into a single array for processing
  const getAllEmails = () => {
    const allEmails = [];
    itemsToRender.forEach((thread) => {
      thread.forEach((email) => {
        allEmails.push(email);
      });
    });
    return getRemainingEmailsToProcess(allEmails, currentDigest?.destinations?.map(({ key }) => key));
  };

  useEffect(() => {
    // Clean up any pending request when component unmounts or processing stops
    return () => {
      // Keep the socket alive globally
      const currentSocket = socketRef.current;
      const currentHandlers = socketHandlersRef.current;
      if (typeof currentHandlers?.dispose === 'function') {
        currentHandlers.dispose();
      } else if (currentSocket && currentHandlers) {
        currentSocket.off("digest:done", currentHandlers.onDone);
        currentSocket.off("digest:failed", currentHandlers.onFailed);
      }
      socketHandlersRef.current = null;
      if (reconciliationTimerRef.current !== null) {
        clearTimeout(reconciliationTimerRef.current);
        reconciliationTimerRef.current = null;
      }
      dispatch(resetJobs());
      batchOutcomeRef.current?.resolve(false);
      batchOutcomeRef.current = null;
      batchContextRef.current = null;
    };
  }, [dispatch]);

  const processAllEmailsForCurrentBatch = useEventCallback(() => processAllEmails());

  useEffect(() => {
    // Process next email when current processing index changes
    if (isProcessingEmailActions) {
      processAllEmailsForCurrentBatch();
    }
  }, [isProcessingEmailActions, processAllEmailsForCurrentBatch]);

  const resolveBatchOutcome = (succeeded) => {
    batchOutcomeRef.current?.resolve(succeeded);
    batchOutcomeRef.current = null;
  };

  const startProcessing = ({
    preserveFailures = false,
    digest = currentDigest,
    items,
  } = {}) => {
    if (batchOutcomeRef.current) return batchOutcomeRef.current.promise;
    const emails = Array.isArray(items)
      ? getRemainingEmailsToProcess(items, digest?.destinations?.map(({ key }) => key))
      : getAllEmails();

    if (emails.length === 0) {
      dispatch(clearManualMailFailures());
      return Promise.resolve(true);
    }

    let resolve;
    const promise = new Promise((settle) => { resolve = settle; });
    batchOutcomeRef.current = { promise, resolve };
    batchFailedRef.current = false;
    preserveFailuresRef.current = preserveFailures === true;
    batchContextRef.current = { digest, emails };
    setEmailsToProcess(emails);
    setCurrentEmailProcessingIndex(0);
    setIsProcessingEmailActions(true);
    setProcessedEmails([]);
    return promise;
  };

  const stopProcessingEmails = () => {
    setIsProcessingEmailActions(false);
    setCurrentEmailProcessingIndex(null);
    setEmailsToProcess([]);
    batchContextRef.current = null;
  };

  const processAllEmails = async () => {
    const batchContext = batchContextRef.current;
    const digestId = batchContext?.digest?.id;
    const mailbox = batchContext?.digest?.mailbox;
    const batchEmails = batchContext?.emails ?? [];
    if (
      typeof digestId !== "string"
      || digestId.length === 0
      || typeof mailbox?.id !== "string"
      || mailbox.id.length === 0
    ) {
      stopProcessingEmails();
      resolveBatchOutcome(false);
      return;
    }

    let response;
    let socket;
    let activeTerminalHandlers = 0;
    let listenersDetached = false;
    let acceptTerminalEvents = false;
    let reconciliationInFlight = false;
    let retainBatchRequestForRecovery = false;
    const reconciliationDeadline = Date.now() + JOB_RECONCILIATION_TIMEOUT_MS;
    const pendingTerminalEvents = new Map();

    const cancelReconciliation = () => {
      if (reconciliationTimerRef.current !== null) {
        clearTimeout(reconciliationTimerRef.current);
        reconciliationTimerRef.current = null;
      }
    };

    const rememberTerminalEvent = (type, payload) => {
      if (!payload?.jobId) return;
      if (pendingTerminalEvents.size >= 100 && !pendingTerminalEvents.has(payload.jobId)) {
        pendingTerminalEvents.delete(pendingTerminalEvents.keys().next().value);
      }
      pendingTerminalEvents.set(payload.jobId, { type, payload });
    };

    const detachListeners = () => {
      if (listenersDetached) return;
      listenersDetached = true;
      cancelReconciliation();
      if (!socket) return;
      socket.off("digest:done", onDone);
      socket.off("digest:failed", onFailed);
      if (socketRef.current === socket) socketRef.current = null;
      socketHandlersRef.current = null;
    };

    const claimTrackedJob = (jobId) => {
      const jobIndex = jobIdsRef.current.indexOf(jobId);
      if (jobIndex === -1) return false;
      jobIdsRef.current.splice(jobIndex, 1);
      activeTerminalHandlers += 1;
      return true;
    };

    const finishTerminalHandler = () => {
      activeTerminalHandlers -= 1;
      if (
        listenersDetached
        || jobIdsRef.current.length !== 0
        || activeTerminalHandlers !== 0
      ) return;

      stopProcessingEmails();
      detachListeners();
      dispatch(resetJobs());
      const succeeded = !batchFailedRef.current;
      if (succeeded) dispatch(clearManualMailFailures());
      if (!retainBatchRequestForRecovery) batchRequestRef.current = null;
      resolveBatchOutcome(succeeded);
    };

    // job-complete listener
    const onDone = async ({ jobId, result }) => {
      if (!acceptTerminalEvents) {
        rememberTerminalEvent('done', { jobId, result });
        return;
      }
      if (!claimTrackedJob(jobId)) return; // ignore old or duplicate batches

      try {
        await reconcileCompletedJobResult({ digestId, result });

        // Then update Redux once with all changes
        dispatch(jobDone({ jobId, result }));
        dispatch(
          setDigestServerHead({
            digestId,
            head: { jobId, updatedAt: Date.now(), source: "manual" },
          }),
        );
      } catch (_error) {
        // The provider job is complete, but browser-side reconciliation is not.
        // Retain the request identity so recovery re-reads this settled result
        // instead of enqueueing the provider mutations a second time.
        batchFailedRef.current = true;
        retainBatchRequestForRecovery = true;
        dispatch(jobFail({
          jobId,
          error: normalizeSafeMailFailure(),
        }));
      } finally {
        finishTerminalHandler();
      }
    };

    // job-failed listener
    const onFailed = ({ jobId, error } = {}) => {
      if (!acceptTerminalEvents) {
        rememberTerminalEvent('failed', { jobId, error });
        return;
      }
      if (!claimTrackedJob(jobId)) return;
      batchFailedRef.current = true;
      dispatch(jobFail({ jobId, error: normalizeSafeMailFailure(error) }));
      finishTerminalHandler();
    };

    const failUnreconciledJobs = () => {
      // A later user recovery must reuse the same request identity: these jobs
      // may still be active even though browser reconciliation timed out.
      retainBatchRequestForRecovery = true;
      for (const jobId of [...jobIdsRef.current]) {
        onFailed({ jobId, error: normalizeSafeMailFailure() });
      }
    };

    const reconcilePendingJobs = async () => {
      reconciliationTimerRef.current = null;
      if (listenersDetached || reconciliationInFlight || jobIdsRef.current.length === 0) return;
      if (Date.now() >= reconciliationDeadline) {
        failUnreconciledJobs();
        return;
      }

      reconciliationInFlight = true;
      try {
        const response = await getEmailActionStatusesService([...jobIdsRef.current]);
        const statuses = Array.isArray(response?.jobIds) ? response.jobIds : [];
        for (const status of statuses) {
          if (status?.state === 'completed') {
            await onDone({ jobId: status.jobId, result: status.result });
          } else if (status?.state === 'failed') {
            onFailed({ jobId: status.jobId, error: status.error });
          }
        }
      } catch (_error) {
        // Socket delivery may still settle the batch. A bounded later poll
        // retries status reconciliation without exposing transport details.
      } finally {
        reconciliationInFlight = false;
        if (!listenersDetached && jobIdsRef.current.length > 0) {
          reconciliationTimerRef.current = setTimeout(
            reconcilePendingJobs,
            JOB_RECONCILIATION_INTERVAL_MS,
          );
        }
      }
    };

    const scheduleReconciliation = () => {
      if (
        listenersDetached
        || reconciliationTimerRef.current !== null
        || jobIdsRef.current.length === 0
      ) return;
      reconciliationTimerRef.current = setTimeout(
        reconcilePendingJobs,
        JOB_RECONCILIATION_INTERVAL_MS,
      );
    };

    try {
      const requestKey = JSON.stringify([
        digestId,
        mailbox.id,
        batchEmails,
      ]);
      if (batchRequestRef.current?.key !== requestKey) {
        batchRequestRef.current = { key: requestKey, id: newBatchRequestId() };
      }

      // Subscribe before enqueueing. Terminal events that beat the HTTP response
      // are buffered until the returned job ids establish this request's scope.
      await ensureSocket(getToken);
      socket = getSocket();
      if (!socket) throw new Error('Mail job socket is unavailable');
      socket.on("digest:done", onDone);
      socket.on("digest:failed", onFailed);
      socketRef.current = socket;
      socketHandlersRef.current = { onDone, onFailed, dispose: detachListeners };

      response = await queueUserEmailActionService(
        assistant.id,
        batchEmails,
        { digestId, mailbox, batchRequestId: batchRequestRef.current.id },
      );
    } catch (error) {
      detachListeners();
      const safeFailure = normalizeSafeMailFailure(
        pickSafeMailFailureFields(error?.response?.data),
      );
      dispatch((preserveFailuresRef.current ? addRetryJobs : addJobs)([QUEUE_REQUEST_JOB_ID]));
      dispatch(setManualMailFailureMailbox(mailbox.id));
      dispatch(jobFail({ jobId: QUEUE_REQUEST_JOB_ID, error: safeFailure }));
      dispatch(resetJobs());
      stopProcessingEmails();
      resolveBatchOutcome(false);
      return;
    }

    if (response?.blocked) {
      batchRequestRef.current = null;
      detachListeners();
      stopProcessingEmails();
      resolveBatchOutcome(false);
      return;
    }

    const queuedJobs = Array.isArray(response?.jobIds) ? response.jobIds : [];
    jobIdsRef.current = queuedJobs.map((job) => job.jobId ?? job);
    dispatch((preserveFailuresRef.current ? addRetryJobs : addJobs)([...jobIdsRef.current]));
    dispatch(setManualMailFailureMailbox(mailbox.id));
    acceptTerminalEvents = true;

    if (jobIdsRef.current.length === 0) {
      batchRequestRef.current = null;
      detachListeners();
      dispatch(resetJobs());
      stopProcessingEmails();
      dispatch(clearManualMailFailures());
      resolveBatchOutcome(true);
      return;
    }

    for (const queuedJob of queuedJobs) {
      if (queuedJob?.state === 'completed') {
        await onDone({ jobId: queuedJob.jobId, result: queuedJob.result });
      } else if (queuedJob?.state === 'failed') {
        onFailed({ jobId: queuedJob.jobId, error: queuedJob.error });
      }
    }

    const earlyTerminalEvents = [...pendingTerminalEvents.values()];
    pendingTerminalEvents.clear();
    for (const { type, payload } of earlyTerminalEvents) {
      if (type === 'done') {
        await onDone(payload);
      } else {
        onFailed(payload);
      }
    }
    scheduleReconciliation();
  };

  const applyEmailUpdate = async (event) => {
    dispatch(updateDigestContentItem(event));

    // Create a task if the email has a task action
    if (
      event.updates?.actions &&
      Array.isArray(event.updates.actions) &&
      event.updates.actions.some((action) => action.type === "create-a-task")
    ) {
      const taskId = event.updates.actions.find(
        (action) => action.type === "create-a-task",
      ).metaData.taskId;
      const newTask = await getTaskByIdService(taskId);

      dispatch(createTask(newTask));
    }

    // Create a maintenance ticket if the email has a maintenance ticket action
    if (
      event.updates?.actions &&
      Array.isArray(event.updates.actions) &&
      event.updates.actions.some(
        (action) => action.type === "create-a-maintenance-ticket",
      )
    ) {
      const mtAction = event.updates.actions.find(
        (action) => action.type === "create-a-maintenance-ticket",
      );
      const newMaintenanceTicket =
        mtAction.metaData.maintenanceTicket ||
        (mtAction.metaData.maintenanceTicketId
          ? { id: mtAction.metaData.maintenanceTicketId }
          : null);
      if (newMaintenanceTicket?.id) {
        dispatch(createMaintenanceTicket(newMaintenanceTicket));
      }
    }

    // Add email file to project if the email has an add to project action
    if (
      event.updates?.actions &&
      Array.isArray(event.updates.actions) &&
      event.updates.actions.some((action) => action.type === "add-to-project")
    ) {
      const addToProjectAction = event.updates.actions.find(
        (action) => action.type === "add-to-project",
      );
      const { emailFileId, chatId, mailIntegrationId } = addToProjectAction.metaData;

      dispatch(
        addEmailFileId({
          emailId: event.contentId,
          chatId,
          fileId: emailFileId,
          mailIntegrationId,
        }),
      );
    }

    for (const action of event.updates.actions) {
      trackEvent("email_action_processed", {
        action_type: action.type,
        actor: action.actor,
      });
    }

    setCurrentEmailProcessingIndex((prev) => prev + 1);
    setProcessedEmails((prev) => [...prev, event.contentId]);
  };

  const reconcileCompletedJobResult = ({ digestId, result }) => {
    if (!Array.isArray(result)) return Promise.reject(new Error('Invalid email job result'));
    const events = result.flatMap((processedEmail) => {
      if (!processedEmail || typeof processedEmail !== 'object') {
        throw new Error('Invalid email job result');
      }
      // A skipped email genuinely has no updates, and updateDigestContentItem merges
      // with { ...existing, ...updates } — queueing one would blank the card's labels
      // and summary, stripping the URGENT marking off mail the user just filed by hand.
      if (processedEmail.skipped) return [];
      return [{
        digestId,
        contentId: processedEmail.emailId,
        updates: {
          archived: processedEmail.emailArchived,
          labels: processedEmail.newEmailLabels,
          actions: processedEmail.executedActions,
          aiSummary: processedEmail.aiSummary,
        },
      }];
    });

    const reconciliation = eventProcessingRef.current.then(async () => {
      if (events.length === 0) return;
      setIsProcessingEvents(true);
      try {
        for (let index = 0; index < events.length; index += 1) {
          await applyEmailUpdate(events[index]);
          if (index < events.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
        }
      } finally {
        setIsProcessingEvents(false);
      }
    });
    eventProcessingRef.current = reconciliation.catch(() => {});
    return reconciliation;
  };

  return {
    processAllEmails: startProcessing,
    getAllEmails: () => emailsToProcess,
    isProcessingEmailActions,
    isProcessingEvents,
    currentEmailProcessingIndex,
    processedEmails,
  };
};
